// Razorpay Standard Checkout helper.
//
// Loads checkout.js on demand (so index.html and prerender.js are untouched),
// asks the server to price the cart and create the order, opens the payment
// window, then has the server verify the signature. Only a verified payment
// counts as paid.
//
// The server prices the order itself from the cart and pincode. The browser
// only says what it was showing, so a stale page is refused rather than
// charged a different amount.

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
let scriptPromise = null;

function loadCheckout() {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = CHECKOUT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => {
      scriptPromise = null;
      resolve(false);
    };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

async function postJSON(url, body) {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data.error || `Request failed (${r.status})`);
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Opens Razorpay checkout.
 *   lines          cart lines, exactly as useCart() returns them
 *   pincode        delivery pincode
 *   quoteRef       CC-... reference
 *   expectedTotal  the total on screen, in rupees
 *   customer       { name, phone, email }
 *   wantsInstall   installation requested (surveyed and quoted separately)
 *   details        { email, address, access, addressType, gstin,
 *                    preferredDay, exchange } carried in the order notes
 *   plan           'full' (default) or 'booking': pay the delivery charge now,
 *                  the balance by link before dispatch
 *   offer          offer code (team's offer email or the Diwali welcome
 *                  pop-up), checked on the server
 *   address        { house, area } for the welcome offer's first-order check
 *   onPaid         (verifyResult) => void, only after server verification
 *   onFailed       (message) => void, an attempt failed; the window stays open
 *   onCancelled    () => void, customer closed the window
 *   onError        (message) => void, could not start, or could not verify
 */
export async function startPayment({
  lines,
  pincode,
  quoteRef,
  expectedNow,
  expectedTotal,
  customer = {},
  wantsInstall = false,
  details = {},
  plan = 'full',
  offer = '',
  address = {},
  onPaid,
  onFailed,
  onCancelled,
  onError,
}) {
  const loaded = await loadCheckout();
  if (!loaded) {
    onError && onError('The payment window did not load. Check your connection and try again.');
    return;
  }

  let order;
  try {
    order = await postJSON('/api/create-order', {
      lines,
      pincode,
      ref: quoteRef,
      expectedNow,
      expectedTotal,
      customer: { name: customer.name, phone: customer.phone },
      wantsInstall: Boolean(wantsInstall),
      details,
      plan,
      offer,
      address,
    });
  } catch (err) {
    // err.data.dropOffer: the offer does not apply to this order.
    onError && onError(err.message, err.data || {});
    return;
  }

  const rzp = new window.Razorpay({
    key: order.key_id,
    order_id: order.order_id,
    amount: order.amount,
    currency: order.currency,
    name: 'Citi Cool Corporation',
    description: order.plan === 'booking'
      ? `Booking for order ${quoteRef || ''}`.trim()
      : quoteRef ? `Order ${quoteRef}` : 'AC order',
    prefill: { name: customer.name, email: customer.email, contact: customer.phone },
    notes: { quote_ref: quoteRef || '' },
    theme: { color: '#2563eb' },
    handler: async (response) => {
      try {
        const result = await postJSON('/api/verify-payment', {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        onPaid && onPaid({
          ...result,
          paidNow: order.payNow ?? order.total,
          total: order.total,
          balance: order.balance || 0,
          plan: order.plan || 'full',
          offer: order.offer || '',
        });
      } catch (err) {
        onError &&
          onError(
            `Your payment went through but we could not confirm it here. Call us with payment ID ${response.razorpay_payment_id} and we will confirm it.`,
          );
      }
    },
    modal: {
      ondismiss: () => onCancelled && onCancelled(),
    },
  });

  rzp.on('payment.failed', (resp) => {
    const msg = (resp && resp.error && resp.error.description) || 'Payment failed.';
    onFailed && onFailed(msg);
  });

  rzp.open();
}

/**
 * Opens Razorpay checkout for an HVAC consultation (/hvac-consultant).
 * The server prices it from src/data/consultation.js; the browser only says
 * which service, how many sq ft and where.
 *   service        'call' | 'visit' | 'heatload' | 'design'
 *   sqft           area, for heat load and end-to-end design
 *   type           'home' | 'commercial', for end-to-end design
 *   pincode        site pincode, for site visit and design
 *   customer       { name, phone }
 *   source         where the customer came from, for the order notes
 *   onPaid         ({ ref, payment_id, total }) => void, only after server verification
 *   onFailed, onCancelled, onError as for startPayment
 */
export async function startConsultPayment({
  service,
  sqft,
  type,
  pincode,
  customer = {},
  source = '',
  onPaid,
  onFailed,
  onCancelled,
  onError,
}) {
  const loaded = await loadCheckout();
  if (!loaded) {
    onError && onError('The payment window did not load. Check your connection and try again.');
    return;
  }

  let order;
  try {
    order = await postJSON('/api/consult-order', {
      action: 'create',
      service,
      sqft,
      type,
      pincode,
      source,
      customer: { name: customer.name, phone: customer.phone },
    });
  } catch (err) {
    onError && onError(err.message);
    return;
  }

  const rzp = new window.Razorpay({
    key: order.key_id,
    order_id: order.order_id,
    amount: order.amount,
    currency: order.currency,
    name: 'Citi Cool Corporation',
    description: `HVAC consultation: ${order.service_name}`,
    prefill: { name: customer.name, contact: customer.phone },
    notes: { quote_ref: order.ref },
    theme: { color: '#2563eb' },
    handler: async (response) => {
      try {
        const result = await postJSON('/api/consult-order', {
          action: 'verify',
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        onPaid && onPaid({ ...result, total: order.total, service_name: order.service_name });
      } catch (err) {
        onError &&
          onError(
            `Your payment went through but we could not confirm it here. Call us with payment ID ${response.razorpay_payment_id} and we will confirm it.`,
          );
      }
    },
    modal: {
      ondismiss: () => onCancelled && onCancelled(),
    },
  });

  rzp.on('payment.failed', (resp) => {
    const msg = (resp && resp.error && resp.error.description) || 'Payment failed.';
    onFailed && onFailed(msg);
  });

  rzp.open();
}
